import { useMemo, useState } from 'react';
import { Database, Grid3x3, Layers, List } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  inferLibraryKind,
  useTemplatesQuery,
  type LibraryKind,
  type TemplateSummary,
} from '@/lib/api/methodology-library';
import { FrameworkCard } from './framework-card';

/**
 * LibraryGrid — the four-section template library renderer for the Firm
 * Settings → Methodology library screen (T-0111 / E01-F04 + F09).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/screens-firm.js →
 * firmLibrary()`: groups firm templates into four "kinds" (Framework /
 * Framework overlay / Register schema / Widget defaults) and renders
 * each as its own section. Inside a section, every template renders as
 * a `<FrameworkCard>` with the latest version selected by default.
 *
 * The TA backend currently keeps everything in a single
 * `methodology_templates` table; the kind is a UI-only inference (see
 * `inferLibraryKind` in `lib/api/methodology-library.ts`). When the
 * backend grows a `kind` column the section logic collapses to a simple
 * groupBy on that field.
 *
 * The grid also surfaces a "Widget defaults summary" sub-section which
 * lists every Framework / Framework overlay row alongside its current
 * widget-defaults state — for v1 this is a TODO marker per the ticket
 * risks-and-unknowns ("framework definitions may be incomplete on
 * backend") since the GET /api/v1/templates response doesn't include
 * widget-default snapshots. T-0114 will land the proper editor modal.
 */

interface LibraryGridProps {
  /** Toggle from the screen header — when true retired templates are included in the query. */
  includeRetired: boolean;
  /**
   * Optional search filter — applied client-side on `template.name`.
   * Trimmed + lowercased before compare.
   */
  searchFilter: string;
  /** Called when the consultant clicks "Edit widget defaults" on a framework card (T-0114). */
  onEditWidgets: (frameworkVersionId: string) => void;
}

const KIND_ORDER: LibraryKind[] = [
  'Framework',
  'Framework overlay',
  'Register schema',
  'Widget defaults',
];

const KIND_ICON: Record<LibraryKind, typeof Grid3x3> = {
  Framework: Grid3x3,
  'Framework overlay': Layers,
  'Register schema': List,
  'Widget defaults': Database,
};

const KIND_DESCRIPTION: Record<LibraryKind, string> = {
  Framework: 'Core methodology shapes — RAID register, posture rollup, base validation rules.',
  'Framework overlay': 'Per-engagement variants layered on top of a framework.',
  'Register schema': 'Schema shapes for each register type (risk, issue, action, dependency).',
  'Widget defaults':
    'Aggregated default-widget rules consumed by the engagement methodology screen.',
};

export function LibraryGrid({ includeRetired, searchFilter, onEditWidgets }: LibraryGridProps) {
  const query = useTemplatesQuery({ includeRetired });
  // selectedVersions keyed by template_id. Null = use latest.
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string | null>>({});

  const filtered: TemplateSummary[] = useMemo(() => {
    const items = query.data ?? [];
    const needle = searchFilter.trim().toLowerCase();
    if (needle.length === 0) return items;
    return items.filter((t) => t.name.toLowerCase().includes(needle));
  }, [query.data, searchFilter]);

  const byKind: Record<LibraryKind, TemplateSummary[]> = useMemo(() => {
    const out: Record<LibraryKind, TemplateSummary[]> = {
      Framework: [],
      'Framework overlay': [],
      'Register schema': [],
      'Widget defaults': [],
    };
    for (const t of filtered) {
      out[inferLibraryKind(t)].push(t);
    }
    return out;
  }, [filtered]);

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading methodology library…
      </div>
    );
  }
  if (query.error) {
    return <ErrorMessage error={query.error} />;
  }

  const total = filtered.length;
  if (total === 0) {
    return (
      <EmptyState
        title="No firm templates"
        description={
          searchFilter.trim().length > 0
            ? `No templates match "${searchFilter.trim()}". Clear the filter to see the full library.`
            : 'No firm templates have been authored yet. Author one with POST /api/v1/templates or promote an engagement-derived framework via the curation queue.'
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {KIND_ORDER.map((kind) => {
        const rows = byKind[kind];
        const KindIcon = KIND_ICON[kind];
        return (
          <section key={kind} aria-label={`${kind}s`}>
            <div className="mb-2 flex items-baseline gap-3">
              <div className="inline-flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ink-500">
                <KindIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {kind}s
              </div>
              <span className="text-meta text-ink-300">{rows.length}</span>
            </div>
            <p className="mb-3 text-meta text-ink-500">{KIND_DESCRIPTION[kind]}</p>
            {rows.length === 0 ? (
              <div className="rounded-card border border-dashed border-ink-200 bg-paper-50 p-4 text-meta text-ink-500">
                No templates in this category yet.
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((t) => (
                  <FrameworkCard
                    key={t.id}
                    template={t}
                    kind={kind}
                    selectedVersionId={selectedVersions[t.id] ?? null}
                    onSelectVersion={(versionId) =>
                      setSelectedVersions((prev) => ({ ...prev, [t.id]: versionId }))
                    }
                    onEditWidgets={onEditWidgets}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      <section aria-label="Widget defaults summary">
        <div className="mb-2 flex items-baseline gap-3">
          <div className="inline-flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ink-500">
            <Database className="h-3.5 w-3.5" aria-hidden="true" />
            Widget defaults summary
          </div>
        </div>
        <p className="mb-3 text-meta text-ink-500">
          Widget-default rules across every framework. Each framework version keeps its own; open
          one with Edit on its card.
        </p>
        <div className="rounded-card border border-ink-100 bg-paper-50 p-4 text-meta text-ink-500">
          Widget defaults belong to the framework version an engagement is bound to, so a change
          applies to engagements that use that version.
        </div>
      </section>
    </div>
  );
}
